import { useApiRef } from '@/core/api/useApi'
import { useTokenStore } from '@/store/useTokenStore'
import { useMachine } from '@xstate/react'
import { useEffect, useMemo } from 'react'
import { assign, fromPromise, setup } from 'xstate'
import { resolveEmailError } from './emailErrorUtils'

// ─── Context ──────────────────────────────────────────────────────────────

type HomeEmailContext = {
  isAuthenticated: boolean
  isAuthInitialized: boolean
  isEmailActivated: boolean | null
  emailForVerification: string | null
  emailRegistrationError: string | null
  isEmailRegistrationSkipped: boolean
  emailRegistrationSkipKey: string
  /** メニューの「メールアドレスの登録/変更」経由で開いた場合 true */
  isEmailChangeMode: boolean
}

// ─── Input ────────────────────────────────────────────────────────────────

type HomeEmailInput = {
  isAuthenticated: boolean
  isAuthInitialized: boolean
  isEmailRegistrationSkipped: boolean
  emailRegistrationSkipKey: string
}

// ─── Events ───────────────────────────────────────────────────────────────

type HomeEmailEvent =
  | { type: 'AUTH_READY'; isAuthenticated: boolean }
  | { type: 'EMAIL_REGISTRATION_SKIP' }
  | { type: 'EMAIL_REGISTRATION_PROCEED' }
  | { type: 'EMAIL_INPUT_SUBMIT'; email: string }
  | { type: 'CODE_INPUT_SUBMIT'; code: string }
  | { type: 'CODE_RESEND' }
  | { type: 'EMAIL_INPUT_BACK' }
  | { type: 'EMAIL_REGISTRATION_CLOSE' }
  | { type: 'EMAIL_CHANGE_BUTTON_PRESSED' }

// ─── Machine Base ─────────────────────────────────────────────────────────

const mynaviHomeEmailBase = setup({
  types: {
    context: {} as HomeEmailContext,
    events: {} as HomeEmailEvent,
    input: {} as HomeEmailInput,
  },
  actors: {
    fetchEmailCheck: fromPromise<boolean | null, { isAuthenticated: boolean }>(async () => null),
    sendEmail: fromPromise<unknown, { email: string }>(async () => undefined),
    verifyCode: fromPromise<unknown, { email: string; code: string }>(async () => undefined),
    resendCode: fromPromise<unknown, { email: string }>(async () => undefined),
  },
  guards: {
    isAuthInitialized: ({ context }) => context.isAuthInitialized,
  },
}).createMachine({
  id: 'mynaviHomeEmail',
  initial: 'waitingForAuth',
  context: ({ input }) => ({
    isAuthenticated: input.isAuthenticated,
    isAuthInitialized: input.isAuthInitialized,
    isEmailActivated: null,
    emailForVerification: null,
    emailRegistrationError: null,
    isEmailRegistrationSkipped: input.isEmailRegistrationSkipped,
    emailRegistrationSkipKey: input.emailRegistrationSkipKey,
    isEmailChangeMode: false,
  }),
  states: {
    // auth 確定前の待機（getState() 時点でまだ loading だったケース用）
    waitingForAuth: {
      always: [{ guard: 'isAuthInitialized', target: 'checkingEmail' }],
      on: {
        AUTH_READY: {
          target: 'checkingEmail',
          actions: assign({
            isAuthenticated: ({ event }) => event.isAuthenticated,
            isAuthInitialized: () => true,
          }),
        },
      },
    },

    // メール有効化状態チェック
    checkingEmail: {
      invoke: {
        src: 'fetchEmailCheck',
        input: ({ context }) => ({ isAuthenticated: context.isAuthenticated }),
        onDone: {
          target: 'idle',
          actions: assign({ isEmailActivated: ({ event }) => event.output }),
        },
        onError: { target: 'idle' },
      },
    },

    // 通常表示（未登録かつ未スキップなら自動遷移）
    idle: {
      always: [
        {
          guard: ({ context }) =>
            context.isAuthenticated && context.isEmailActivated === false && !context.isEmailRegistrationSkipped,
          target: 'emailRegistration.prompt',
        },
      ],
      on: {
        EMAIL_CHANGE_BUTTON_PRESSED: {
          target: 'emailRegistration.inputEmail',
          actions: assign({ isEmailChangeMode: true }),
        },
      },
    },

    // メール登録フロー
    emailRegistration: {
      initial: 'prompt',
      states: {
        prompt: {
          on: {
            EMAIL_REGISTRATION_SKIP: {
              target: '#mynaviHomeEmail.idle',
              actions: assign({
                isEmailRegistrationSkipped: ({ context }) => {
                  try {
                    localStorage.setItem(context.emailRegistrationSkipKey, '1')
                  } catch {
                    // ignore
                  }
                  return true
                },
              }),
            },
            EMAIL_REGISTRATION_PROCEED: 'inputEmail',
            EMAIL_CHANGE_BUTTON_PRESSED: {
              target: 'inputEmail',
              actions: assign({ isEmailChangeMode: true }),
            },
          },
        },
        inputEmail: {
          on: {
            EMAIL_INPUT_SUBMIT: {
              target: 'sendingEmail',
              actions: assign({
                emailForVerification: ({ event }) => event.email,
                emailRegistrationError: null,
              }),
            },
            EMAIL_INPUT_BACK: {
              target: '#mynaviHomeEmail.idle',
              actions: assign({ isEmailChangeMode: false }),
            },
          },
        },
        sendingEmail: {
          invoke: {
            src: 'sendEmail',
            input: ({ context }) => {
              if (!context.emailForVerification) throw new Error('emailForVerification is not set')
              return { email: context.emailForVerification }
            },
            onDone: {
              target: 'inputCode',
              actions: assign({ emailRegistrationError: null }),
            },
            onError: {
              target: 'inputEmail',
              actions: assign({
                emailRegistrationError: ({ event }) => resolveEmailError(event.error),
              }),
            },
          },
        },
        inputCode: {
          on: {
            CODE_INPUT_SUBMIT: {
              target: 'sendingCode',
              actions: assign({ emailRegistrationError: null }),
            },
            CODE_RESEND: 'resendingCode',
            EMAIL_INPUT_BACK: 'inputEmail',
          },
        },
        sendingCode: {
          invoke: {
            src: 'verifyCode',
            input: ({ context, event }) => {
              if (event.type !== 'CODE_INPUT_SUBMIT') throw new Error('unexpected event')
              if (!context.emailForVerification) throw new Error('emailForVerification is not set')
              return { email: context.emailForVerification, code: event.code }
            },
            onDone: {
              target: 'complete',
              actions: assign({ isEmailActivated: true, emailRegistrationError: null }),
            },
            onError: {
              target: 'inputCode',
              actions: assign({
                emailRegistrationError: ({ event }) => resolveEmailError(event.error),
              }),
            },
          },
        },
        resendingCode: {
          invoke: {
            src: 'resendCode',
            input: ({ context }) => {
              if (!context.emailForVerification) throw new Error('emailForVerification is not set')
              return { email: context.emailForVerification }
            },
            onDone: { target: 'inputCode' },
            onError: { target: 'inputCode' },
          },
        },
        complete: {
          on: {
            EMAIL_REGISTRATION_CLOSE: {
              target: '#mynaviHomeEmail.idle',
              actions: assign({ emailForVerification: null, isEmailChangeMode: false }),
            },
          },
        },
      },
    },
  },
})

// ─── Hook ─────────────────────────────────────────────────────────────────

export const useMynaviHomeEmailStateMachine = ({
  emailRegistrationSkipKey,
}: {
  emailRegistrationSkipKey: string
}) => {
  const apiRef = useApiRef()

  const isEmailRegistrationSkipped = (() => {
    try {
      return localStorage.getItem(emailRegistrationSkipKey) === '1'
    } catch {
      return false
    }
  })()

  // biome-ignore lint/correctness/useExhaustiveDependencies: actors use refs
  const machine = useMemo(
    () =>
      mynaviHomeEmailBase.provide({
        actors: {
          fetchEmailCheck: fromPromise(async ({ input: actorInput }: { input: { isAuthenticated: boolean } }) => {
            if (!actorInput.isAuthenticated) return null
            const result = await apiRef.current.auth.fetchMynaviEmailCheck()
            return result.status === 'verified'
          }),
          sendEmail: fromPromise<unknown, { email: string }>(async ({ input: actorInput }) => {
            const result = await apiRef.current.auth.postMynaviEmail(actorInput.email)
            if (result.status === 'error') throw new Error(result.message ?? 'メール送信に失敗しました')
            return result
          }),
          verifyCode: fromPromise<unknown, { email: string; code: string }>(async ({ input: actorInput }) => {
            const result = await apiRef.current.auth.postMynaviEmailVerify(actorInput.email, actorInput.code)
            if (result.status === 'error') throw new Error(result.message ?? '認証コードの検証に失敗しました')
            return result
          }),
          resendCode: fromPromise<unknown, { email: string }>(async ({ input: actorInput }) => {
            const result = await apiRef.current.auth.postMynaviEmailResend(actorInput.email)
            if (result.status === 'error') throw new Error(result.message ?? '再送に失敗しました')
            return result
          }),
        },
      }),
    [],
  )

  const [state, send] = useMachine(machine, {
    input: (() => {
      const tokenState = useTokenStore.getState()
      return {
        isAuthenticated: !tokenState.loading && tokenState.idToken !== null,
        isAuthInitialized: !tokenState.loading,
        isEmailRegistrationSkipped,
        emailRegistrationSkipKey,
      }
    })(),
  })

  // loading 解消時のフォールバック（getState() 時点でまだ loading だったケース）
  const idToken = useTokenStore((s) => s.idToken)
  const tokenLoading = useTokenStore((s) => s.loading)
  useEffect(() => {
    if (tokenLoading) return
    send({ type: 'AUTH_READY', isAuthenticated: idToken !== null })
  }, [tokenLoading, idToken, send])

  return {
    send,
    isEmailRegistrationPromptOpen: state.matches({ emailRegistration: 'prompt' }),
    isEmailInputOpen: state.matches({ emailRegistration: 'inputEmail' }),
    isEmailSending: state.matches({ emailRegistration: 'sendingEmail' }),
    isEmailVerificationOpen: state.matches({ emailRegistration: 'inputCode' }),
    isEmailVerifying: state.matches({ emailRegistration: 'sendingCode' }),
    isEmailResending: state.matches({ emailRegistration: 'resendingCode' }),
    isEmailCompleteOpen: state.matches({ emailRegistration: 'complete' }),
    emailRegistrationError: state.context.emailRegistrationError,
    isEmailChangeMode: state.context.isEmailChangeMode,
  }
}
